/**
 * The unsubmitted aging report (BUILD_PLAN.md section 13.4): each tech's
 * blocks still to log, by how long they've waited: today, 1–3 days, 4–7
 * days, older. It's as of now, so the filters' days don't apply. Each age
 * counts from the tech's own today, in their timezone, and the counts are
 * the ones their day view's To review tile shows: the API ages them the same
 * way.
 *
 * "Download CSV" saves the same rows.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useId } from "react";
import { Link } from "react-router";

import type { AgeBucket, AgeBuckets, AgingRow, TechAging } from "../../api/types";
import { useToday } from "../../app/useSelectedDay";
import { useSession } from "../../app/session-context";
import { Badge, HipaaBadge } from "../../components/ui/Badge";
import { Panel } from "../../components/ui/Panel";
import { formatLongDay, formatShortDay, formatTimeIn } from "../../lib/dates";
import { saveBlob } from "../../lib/download";
import { formatDuration } from "../../lib/durations";
import { possessive } from "../../lib/possessive";
import { count, errorMessage, pillButton, retryOnce, STATUS_TONES } from "./common";
import { useReportFilters } from "./filters";
import { Sources } from "./TechActivityReport";

/** The ages, in order, in the day view's words. */
const AGES: { key: AgeBucket; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "days_1_to_3", label: "1–3 days" },
  { key: "days_4_to_7", label: "4–7 days" },
  { key: "older", label: "Older" },
];

const headCell = "pb-2 pr-3";

function techName(tech: TechAging): string {
  return tech.user.display_name ?? tech.user.email ?? "Unnamed user";
}

function total(buckets: AgeBuckets): number {
  return buckets.today + buckets.days_1_to_3 + buckets.days_4_to_7 + buckets.older;
}

function waited(days: number): string {
  return days === 0 ? "Today" : count(days, "day", "days");
}

/** How many blocks, and how long they come to. */
function Waiting({ blocks, seconds }: { blocks: number; seconds: number }) {
  if (blocks === 0) {
    return <span className="text-muted">0</span>;
  }
  return (
    <span className="flex flex-col">
      <span className="font-semibold tracking-[-0.02em]">
        {String(blocks)}
        <span className="sr-only">{blocks === 1 ? " block, " : " blocks, "}</span>
      </span>
      <span className="text-xs text-muted">{formatDuration(seconds)}</span>
    </span>
  );
}

/** Techs × ages, with every tech's added up at the foot. */
function Summary({ techs }: { techs: TechAging[] }) {
  const sum = (pick: (tech: TechAging) => number) => techs.reduce((n, tech) => n + pick(tech), 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[13.5px]">
        <caption className="sr-only">Blocks still to log, by age, for each tech</caption>
        <thead className="text-[12.5px] font-medium tracking-[0.08em] text-muted uppercase">
          <tr>
            <th scope="col" className={headCell}>
              Tech
            </th>
            {AGES.map((age) => (
              <th key={age.key} scope="col" className={headCell}>
                {age.label}
              </th>
            ))}
            <th scope="col" className="pb-2">
              All
            </th>
          </tr>
        </thead>
        <tbody>
          {techs.map((tech) => (
            <tr key={tech.user.id} className="border-t border-line align-top">
              <th scope="row" className="py-3 pr-3 font-medium">
                {techName(tech)}
              </th>
              {AGES.map((age) => (
                <td key={age.key} className="py-3 pr-3">
                  <Waiting blocks={tech.by_age[age.key]} seconds={tech.seconds_by_age[age.key]} />
                </td>
              ))}
              <td className="py-3">
                <Waiting blocks={total(tech.by_age)} seconds={total(tech.seconds_by_age)} />
              </td>
            </tr>
          ))}
        </tbody>
        {techs.length > 1 ? (
          <tfoot>
            <tr className="border-t border-line align-top">
              <th scope="row" className="py-3 pr-3 font-semibold">
                Every tech
              </th>
              {AGES.map((age) => (
                <td key={age.key} className="py-3 pr-3">
                  <Waiting
                    blocks={sum((tech) => tech.by_age[age.key])}
                    seconds={sum((tech) => tech.seconds_by_age[age.key])}
                  />
                </td>
              ))}
              <td className="py-3">
                <Waiting
                  blocks={sum((tech) => total(tech.by_age))}
                  seconds={sum((tech) => total(tech.seconds_by_age))}
                />
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

function Row({ row, tech }: { row: AgingRow; tech: TechAging }) {
  const zone = tech.user.timezone;
  return (
    <tr className="border-t border-line align-top">
      <td className="py-3 pr-3 whitespace-nowrap">
        <Link
          to={`/?${new URLSearchParams({ date: row.day, user: tech.user.id }).toString()}`}
          title={`Open ${possessive(techName(tech))} day, ${formatLongDay(row.day)}`}
          className="font-medium text-brand-ink hover:underline"
        >
          {formatShortDay(row.day)}
          <span className="sr-only">{`: open ${possessive(techName(tech))} day`}</span>
        </Link>
      </td>
      <td className="py-3 pr-3 whitespace-nowrap">{waited(row.age_days)}</td>
      <td className="py-3 pr-3 font-mono text-xs whitespace-nowrap">
        {`${formatTimeIn(row.started_at, zone)}–${formatTimeIn(row.ended_at, zone)}`}
      </td>
      <td className="py-3 pr-3">
        <span className="flex flex-col gap-1">
          {row.client ? <span className="font-medium">{row.client.name}</span> : <span className="text-muted">No client</span>}
          {row.client?.is_hipaa ? <HipaaBadge /> : null}
        </span>
      </td>
      <td className="py-3 pr-3">
        <Sources sources={row.sources} />
      </td>
      <td className="py-3 pr-3 whitespace-nowrap">
        <span className="font-semibold tracking-[-0.02em]">{formatDuration(row.seconds)}</span>
        {row.duration_changed ? <span className="ml-1.5 text-xs text-muted">changed</span> : null}
      </td>
      <td className="py-3">
        <Badge tone={STATUS_TONES[row.status]}>{row.status_label}</Badge>
      </td>
    </tr>
  );
}

function TechSection({ tech }: { tech: TechAging }) {
  const headingId = useId();
  const name = techName(tech);
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 id={headingId} className="text-base font-[650] tracking-[-0.015em]">
          {name}
        </h3>
        <p className="text-[13px] text-muted">
          {[
            `${count(tech.rows.length, "block", "blocks")} to log`,
            formatDuration(total(tech.seconds_by_age)),
            `Ages from ${formatShortDay(tech.today)}, their today`,
            `Times in ${tech.user.timezone}`,
          ].join(" · ")}
        </p>
      </div>
      {tech.rows.length === 0 ? (
        <p className="rounded-row bg-panel-2 px-4 py-3 text-muted">Nothing waiting to be logged.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13.5px]">
            <caption className="sr-only">{`${possessive(name)} blocks still to log, oldest first`}</caption>
            <thead className="text-[12.5px] font-medium tracking-[0.08em] text-muted uppercase">
              <tr>
                <th scope="col" className={headCell}>
                  Date
                </th>
                <th scope="col" className={headCell}>
                  Waiting
                </th>
                <th scope="col" className={headCell}>
                  Time
                </th>
                <th scope="col" className={headCell}>
                  Client
                </th>
                <th scope="col" className={headCell}>
                  Sources
                </th>
                <th scope="col" className={headCell}>
                  Duration
                </th>
                <th scope="col" className="pb-2">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {tech.rows.map((row) => (
                <Row key={row.block_id} row={row} tech={tech} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function UnsubmittedAgingReport() {
  const { api, me } = useSession();
  const headingId = useId();
  const [filters] = useReportFilters();
  const today = useToday(me.timezone);
  // As of now: the days aren't part of what it asks.
  const asked = { userIds: filters.userIds, clientIds: filters.clientIds };
  const report = useQuery({
    queryKey: ["reports", "unsubmitted-aging", asked],
    queryFn: () => api.getUnsubmittedAgingReport(filters),
    retry: retryOnce,
  });
  const download = useMutation({
    mutationFn: () => api.downloadUnsubmittedAgingCsv(filters),
    onSuccess: (blob) => {
      saveBlob(blob, `unsubmitted-aging-${today}.csv`);
    },
  });

  return (
    <Panel aria-labelledby={headingId} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 id={headingId} className="text-lg font-semibold tracking-tight">
            Unsubmitted aging
          </h2>
          <p className="text-[13.5px] text-muted">
            Blocks still to log, by how long they&apos;ve waited, as each tech&apos;s To review tile counts them.
          </p>
        </div>
        <button
          type="button"
          className={pillButton}
          disabled={!report.isSuccess || download.isPending}
          onClick={() => {
            download.mutate();
          }}
        >
          Download CSV
        </button>
      </div>
      {download.isError ? (
        <p role="alert" className="text-sm text-status-needs-client-fg">
          Not downloaded: {errorMessage(download.error)}
        </p>
      ) : null}
      {report.isPending ? (
        <p role="status" className="text-muted">
          Loading the report…
        </p>
      ) : report.isError ? (
        <p role="alert" className="text-status-needs-client-fg">
          {errorMessage(report.error)}
        </p>
      ) : report.data.techs.length === 0 ? (
        <p className="py-6 text-center text-muted">Nothing waiting to be logged.</p>
      ) : (
        <>
          <Summary techs={report.data.techs} />
          {report.data.techs.map((tech) => (
            <TechSection key={tech.user.id} tech={tech} />
          ))}
        </>
      )}
    </Panel>
  );
}
